<template>
    <div class="receipt-invoice-sr invoice">

        <!-- Invoice Header -->
        <template v-if="!order.isIncoming()">
            <div
                v-if="venue.invoice_header"
                class="ck-content ck-custom"
                v-html="venue.invoice_header"
            />

            <div
                v-else
                class="receipt-header"
            >
                <p>{{ venue.company_name }}</p>
                <p>{{ venue.name }}</p>
                <p>{{ venue.address }}</p>
            </div>
        </template>

        <div class="nonfiscal-heading">
            <p v-if="order.number">{{ $t('receipt.serial_number') + ': ' + order.number }}</p>
            <p v-if="!preview && order.internal_number">
                {{ `${!order.isProforma() ? $t('receipt.internal_number') : $t('receipt.proforma_number')}: ${order.internal_number}` }}
            </p>
            <p v-if="preview && options.internalNumber">
                {{ `${!options.isProforma ? $t('receipt.internal_number') : $t('receipt.proforma_number')}: ${options.internalNumber}` }}
            </p>
            <p v-if="order.due_date">{{ $t('receipt.due_date') }}: {{ order.due_date | date }}</p>
            <p v-if="order.start_date && order.end_date">
                {{ $t('receipt.period') }}: {{ order.start_date | date }} - {{ order.end_date | date }}
            </p>
        </div>

        <receipt-divider
            :text="['Normal','Advance'].includes(invoice.invoiceType) ? $t('receipt.fiscal_receipt') : $t('receipt.nonfiscal_receipt')"
            :large="!['Normal','Advance'].includes(invoice.invoiceType)"
            delimiter="="
        />

        <div class="receipt-subheader">
            <!-- Receipt Details -->
            <div class="receipt-details">
                <p v-for="info in supplierInfo" :key="info">
                    {{ info }}
                </p>
            </div>

            <!-- Invoice Buyer Details -->
            <div v-if="buyerInfo.length" class="receipt-buyer">
                <p><strong>{{ $t('receipt.buyer') }}</strong></p>
                <p v-for="info in buyerInfo" :key="info">
                    {{ info }}
                </p>
            </div>
        </div>

        <receipt-divider
            :text="$t(`receipt.type_${invoice.invoiceType.toLowerCase()}`) + ' ' + $t(`receipt.transaction_${invoice.transactionType.toLowerCase()}`)"
            style="margin-bottom: 12px;"
        />

        <!-- Receipt Items -->
        <table v-if="!venue.wholesale" class="receipt-items">
            <tbody>

                <!-- Heading Row -->
                <tr class="heading-row">
                    <td align="left">{{ $t('receipt.items.number') }}</td>
                    <td align="left">{{ $t('receipt.items.code') }}</td>
                    <td align="left">{{ $t('receipt.items.name') }}</td>
                    <td align="right">{{ $t('receipt.items.unit_price') }}</td>
                    <td align="right">{{ $t('receipt.items.quantity') }}</td>
                    <td align="right">{{ $t('receipt.items.to_pay') }}</td>
                </tr>

                <!-- Order Items -->
                <tr
                    v-for="(item, index) in invoice.items"
                    :key="`invoice-${index}`"
                    :class="{ 'border-bottom': index !== invoice.items.length - 1 }"
                >
                    <td align="left">{{ index + 1 }}.</td>
                    <td align="left">
                        <span v-if="order.items[index] && order.items[index].getProduct()">
                            {{ order.items[index].getProduct().code }}
                        </span>
                    </td>
                    <td align="left" class="name">
                        <span>{{ item.name }}</span>
                        <span>{{ item.labels.map(label => ` (${label})`).join('') }}</span>
                        <span v-if="item.unit">/{{ item.unit }}</span>
                        <div v-if="isAllegra && item.note" v-html="nl2br(item.note)"></div>
                        <div v-if="item.gtin" style="font-size: 11px;">GTIN: {{ item.gtin }}</div>
                    </td>
                    <td align="right">{{ Money.parse(item.unitPrice).toFormat(priceFormat) }}</td>
                    <td align="right">{{ item.quantity }}</td>
                    <td align="right">
                        {{ Money.parse(item.totalAmount).multiply(invoice.transactionType === 'Sale' ? 1 : -1).toFormat(priceFormat) }}
                    </td>
                </tr>

                <tr class="divider">
                    <td :colspan="12" />
                </tr>
            </tbody>
        </table>

        <!-- Receipt Items -->
        <table v-else class="receipt-items">
            <tbody>

                <!-- Heading Row -->
                <tr class="heading-row">
                    <td align="left">{{ $t('receipt.items.number') }}</td>
                    <td align="left">{{ $t('receipt.items.code') }}</td>
                    <td align="left">{{ $t('receipt.items.name') }}</td>
                    <td align="right">{{ $t('receipt.items.quantity') }}</td>
                    <td align="right" width="70px">{{ $t('receipt.items.wholesale.unit_price_before_tax') }}</td>
                    <td align="right">{{ $t('receipt.items.wholesale.tax_rate') }}</td>
                    <td align="right" width="70px">{{ $t('receipt.items.wholesale.unit_price') }}</td>
                    <td align="right">{{ $t('receipt.items.wholesale.discount') }}</td>
                    <td align="right" width="90px">{{ $t('receipt.items.wholesale.unit_price_after_discount') }}</td>
                    <td align="right" width="90px">{{ $t('receipt.items.wholesale.price_before_tax') }}</td>
                    <td align="right" width="70px">{{ $t('receipt.items.wholesale.tax') }}</td>
                    <td align="right" width="80px">{{ $t('receipt.items.wholesale.to_pay') }}</td>
                </tr>

                <!-- Order Items -->
                <template v-for="(item, index) in order.items">
                    <tr
                        :key="`invoice-${index}`"
                        :class="{ 'border-bottom': (!isAllegra || !item.comments.length) && index != order.items.length - 1 }"
                    >
                        <td align="left">{{ index + 1 }}.</td>
                        <td align="left">
                            {{ item.getProduct() && item.getProduct().code ? item.getProduct().code : '' }}
                        </td>
                        <td align="left" class="name">
                            <span>{{ item.name }}</span>
                            <span v-if="item.getTaxRateLabel()"> ({{ item.getTaxRateLabel() }})</span>
                            <span v-if="item.getProduct()">/{{ item.getProduct().getUnitOfMeasureSold() }}</span>
                            <div v-if="item.gtin" style="font-size: 11px;">GTIN: {{ item.gtin }}</div>
                        </td>
                        <td align="right">{{ Math.abs(item.getCount()) }}</td>
                        <td align="right">{{ item.getUnitPriceBeforeTax().toFormat('0,0.0000') }}</td>
                        <td align="right">{{ item.getTaxRate() }}%</td>
                        <td align="right">{{ item.getUnitPriceAfterTax().toFormat(priceFormat) }}</td>
                        <td align="right">{{ item.getDiscountPercent() }}%</td>
                        <td align="right">{{ item.getUnitPriceAfterDiscount().abs().toFormat(priceFormat) }}</td>
                        <td align="right">{{ item.getPriceBeforeTax().toFormat(priceFormat) }}</td>
                        <td align="right">{{ item.getTax().toFormat(priceFormat) }}</td>
                        <td align="right">{{ item.getToPay().toFormat(priceFormat) }}</td>
                    </tr>

                    <tr
                        v-if="isAllegra && item.comments.length" :key="`invoice-item-comment-${index}`"
                        :class="{ 'border-bottom': index != order.items.length - 1 }"
                    >
                        <td></td>
                        <td></td>
                        <td colspan="10">
                            <span style="font-size: 11px;" v-html="nl2br(item.comments[0])"></span>
                        </td>
                    </tr>
                </template>

                <tr class="divider">
                    <td :colspan="12" />
                </tr>
            </tbody>
        </table>

        <receipt-divider
            v-if="!['Normal','Advance'].includes(invoice.invoiceType)"
            :text="$t('receipt.nonfiscal_receipt')"
            :large="true"
            delimiter="="
            style="margin-bottom: 12px;"
        />

        <div class="receipt-footer">
            <div class="footer-item additional-info">
                <p>
                    <span v-if="invoice.invoiceType === 'Advance'" class="code">
                        {{ $t('receipt.time_pos') }}: {{ preview ? moment() : moment(invoice.dateAndTimeOfIssue).tz('Europe/Belgrade') | dateTime }}
                    </span>

                    <span v-if="invoice.referentDocumentNumber" class="code">
                        {{ $t('receipt.ref_number') }}: {{ invoice.referentDocumentNumber }}
                    </span>

                    <span v-if="invoice.referentDocumentDT" class="code mb-3">
                        {{ $t('receipt.ref_time') }}: {{ invoice.referentDocumentDT | dateTime }}
                    </span>

                    <span class="code">{{ $t('receipt.number') }}: {{ invoice.invoiceNumber }}</span>

                    <span class="code">
                        {{ $t('receipt.sdc.number') }}:
                        <span v-if="sdcResponse">{{ sdcResponse.invoiceNumber }}</span>
                        <span v-else>XXXXXXXX-YYYYYYYY-XYZ</span>
                    </span>

                    <span class="code">
                        {{ $t('receipt.sdc.time') }}:
                        <span v-if="sdcResponse">
                            {{ moment(sdcResponse.sdcDateTime).tz('Europe/Belgrade').format('DD.MM.YYYY. HH:mm:ss') }}
                        </span>
                        <span v-else>
                            {{ moment().tz('Europe/Belgrade').format('DD.MM.YYYY. HH:mm:ss') }}
                        </span>
                    </span>

                    <span class="code">
                        {{ $t('receipt.sdc.counter') }}:
                        <span v-if="sdcResponse">{{ sdcResponse.invoiceCounter }}</span>
                        <span v-else>XY/XYZ</span>
                    </span>
                </p>

                <div v-if="invoice.invoiceType === 'Copy' && invoice.transactionType === 'Refund'" class="buyer-signatures">
                    <div class="buyer-signature">
                        <div>___________________________</div>
                        <div>{{ $t('receipt.buyer_signature') }}</div>
                    </div>
                </div>
            </div>

            <div class="footer-item qrcode">
                <template v-if="showQrCode">
                    <qrcode
                        :value="order.generateQRCodeString()"
                        :options="{
                            width: 180,
                            errorCorrectionLevel: 'L',
                            margin: 0,
                            scale: 6,
                        }"
                        tag="img"
                    />
                    <div class="verification-link">
                        <a target="_blank" :href="order.generateQRCodeString()">{{ $t('receipt.verify') }}</a>
                    </div>
                </template>
            </div>

            <div class="footer-item summary">
                <div v-if="invoice.transactionType === 'Sale'" class="summary-item to-pay">
                    <p>{{ $t('receipt.payments.to_pay_sale') }}:</p>
                    <p v-if="sdcResponse" class="amount">
                        {{ summary.totalAmount.toFormat(priceFormat) }}
                    </p>
                    <p v-else class="amount">
                        {{ this.order.getToPay().abs().toFormat(priceFormat) }}
                    </p>
                </div>

                <div v-else class="summary-item to-pay">
                    <p>{{ $t('receipt.payments.to_pay_refund') }}:</p>
                    <p v-if="sdcResponse" class="amount">
                        {{ summary.totalAmount.toFormat(priceFormat) }}
                    </p>
                    <p v-else class="amount">
                        {{ this.order.getToPay().abs().toFormat(priceFormat) }}
                    </p>
                </div>

                <div v-for="(paymentType, index) in paymentTypes" :key="index" class="summary-item">
                    <p>{{ paymentType.display }}:</p>
                    <p class="amount">{{ paymentType.amount.abs().toFormat(priceFormat) }}</p>
                </div>

                <table v-if="sdcResponse" class="tax-items">
                    <tbody>
                        <tr class="heading-row">
                            <td align="left">{{ $t('receipt.taxes.label') }}</td>
                            <td align="center">{{ $t('receipt.taxes.name') }}</td>
                            <td align="right">{{ $t('receipt.taxes.rate') }}</td>
                            <td align="right">{{ $t('receipt.taxes.amount') }}</td>
                        </tr>

                        <tr v-for="(taxItem, index) in sdcResponse.taxItems" :key="index">
                            <td align="left">{{ taxItem.label }}</td>
                            <td align="center">{{ taxItem.categoryName }}</td>
                            <td align="right">{{ taxItem.rate }}%</td>
                            <td align="right">{{ Money.parse(taxItem.amount).toFormat(priceFormat) }}</td>
                        </tr>

                        <tr v-if="venue.wholesale" class="bordered">
                            <td colspan="3">
                                {{ $t('receipt.taxes.total_price_before_tax') }}:
                            </td>
                            <td align="right">
                                {{ summary.totalPriceBeforeTax.toFormat(priceFormat) }}
                            </td>
                        </tr>

                        <tr class="bordered">
                            <td colspan="3">
                                {{ $t('receipt.taxes.total') }}:
                            </td>
                            <td align="right">
                                {{ summary.totalTax.toFormat(priceFormat) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <receipt-divider
            :text="['Normal','Advance'].includes(invoice.invoiceType) ? $t('receipt.fiscal_receipt_end') : $t('receipt.nonfiscal_receipt')"
            :large="!['Normal','Advance'].includes(invoice.invoiceType)"
            delimiter="="
        />

        <div class="nonfiscal-info">
            <p v-if="order.note">
                {{ order.note }}
            </p>

            <p v-if="advanceRefundInvoice">
                {{ $t('receipt.last_advance_receipt') }}:
                {{ advanceRefundInvoice.referentDocumentNumber }}
                {{ moment(advanceRefundInvoice.referentDocumentDT).tz('Europe/Belgrade').format('DD.MM.YYYY.') }}
            </p>

            <p v-if="!venue.taxes_enabled">
                {{ $t('receipt.note') }}: {{ $t('receipt.exempt_from_tax_comment') }}
            </p>

            <div class="signatures">
                <div class="signature">
                    <div>___________________________</div>
                    <div>{{ $t('receipt.delivered_by') }}</div>
                </div>

                <div class="signature">
                    <div>___________________________</div>
                    <div>{{ $t('receipt.received_by') }}</div>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import Client from '@/models/Client';
import latToCyr from '@/utilities/LatToCyr'
import ReceiptDivider from '@/components/print/ReceiptDivider'

export default {
    components: {
        ReceiptDivider
    },

    props: {
        order: {
            type: Object,
            default: () => null
        },
        options: {
            type: Object,
            default: () => {}
        },
        preview: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            priceFormat: '0,0.00',
        };
    },

    computed: {
        venue() {
            return this.options.venue || this.tenant?.venue
        },

        client() {
            return this.order.client || Client.find(this.order.client_id)
        },

        invoice() {
            const order = this.order.$duplicate()
            if(this.options.isProforma) {
                order.status = Order.STATUS_PROFORMA
            }

            return order.register_request || order.getRegisterRequestSr()
        },

        sdcResponse() {
            return this.order.register_response
        },

        paymentTypes() {
            if(this.options.isProforma) {
                if(!this.options.paymentType) return [];

                return [{
                    label: this.$t(`receipt.payments.${this.options.paymentType.toLowerCase()}`),
                    amount: this.order.getLeftToPay()
                }];
            }

            if(this.preview) {
                let paymentTypes = [];
                if(this.order.getPaidAdvance().toUnit() > 0) {
                    paymentTypes.push({
                        display: this.$t('receipt.payments.advance'),
                        amount: this.order.getPaidAdvanceEffective()
                    });
                }

                if(this.order.getLeftToPay().toUnit() !== 0) {
                    paymentTypes.push({
                        display: this.$t('receipt.payments.wiretransfer'),
                        amount: this.order.getLeftToPay()
                    });
                }

                return paymentTypes.filter(type => type.amount.toUnit() != 0);
            }

            const totalPaid = this.order.getPaid().abs();
            const totalAmount = this.order.getToPay().abs();

            const remaining = this.order.isAdvance()
                ? Money.parse(0)
                : Money.parse(totalAmount.toRoundedUnit(2)).subtract(Money.parse(totalPaid.toRoundedUnit(2)))

            let allPaymentTypes = [];
            let paymentTypes = this.order.getPayments();
            const advancePayment = paymentTypes.find(p => p.value === 'advance')

            if(advancePayment) {
                allPaymentTypes.push(advancePayment)
                paymentTypes = paymentTypes.filter(p => p.value !== 'advance')
            }

            if(this.sdcResponse?.advanceTax) {
                allPaymentTypes.push({
                    display: this.$t('receipt.payments.advance_tax'),
                    amount: Money.parse(this.sdcResponse.advanceTax)
                })
            }

            allPaymentTypes = [...allPaymentTypes, ...paymentTypes];

            if(remaining.toUnit() !== 0) {
                allPaymentTypes.push({
                    display: remaining.toUnit() < 0 ? this.$t('receipt.payments.change') : this.$t('receipt.payments.left_to_pay'),
                    amount: remaining.abs()
                })
            }

            return allPaymentTypes
        },

        showQrCode() {
            if(this.preview) return false
            return true
        },

        advanceRefundInvoice() {
            if(!this.order.refersToAdvance()) return null;
            if(!this.order.refers_to[0]) return null;

            const advanceRefund = this.order.refers_to[0];
            return advanceRefund.register_request || advanceRefund.getRegisterRequestSr();
        },

        supplierInfo() {
            let supplierInfo = []
            if(this.sdcResponse) {
                supplierInfo = [
                    `${this.$t('receipt.tax_id_number')}: ${this.sdcResponse.tin}`,
                    `${this.$t('receipt.company')}: ${this.sdcResponse.businessName}`,
                    `${this.$t('receipt.venue')}: ${this.sdcResponse.locationName}`,
                    `${this.$t('receipt.address')}: ${this.sdcResponse.address}`,
                    `${this.$t('receipt.municipality')}: ${this.sdcResponse.district}`,
                ]
            } else if(!this.order.isIncoming()) {
                supplierInfo = [
                    `${this.$t('receipt.tax_id_number')}: ${this.venue.tax_id_number}`,
                    `${this.$t('receipt.company')}: ${this.venue.company_name}`,
                    `${this.$t('receipt.venue')}: ${this.venue.name}`,
                    `${this.$t('receipt.address')}: ${this.venue.address}`,
                ]
            }

            supplierInfo.push(`${this.$t('receipt.cashier')}: ${this.invoice.cashier}`)
            return supplierInfo
        },

        buyerInfo() {
            const buyerInfo = []

            if(!this.order.isIncoming()) {
                if(this.client?.name) {
                    buyerInfo.push(this.client.name);
                }
                if(this.client?.address) {
                    let address = this.client.address;
                    if(this.client.city) address += `, ${this.client.city}`
                    if(this.client.country) address += `, ${this.client.country}`
                    buyerInfo.push(address);
                }
                if(this.client?.registration_number) {
                    buyerInfo.push(this.$t('receipt.registration_number') + ': ' + this.client.registration_number);
                }
            } else {
                buyerInfo.push(this.venue.name);
                buyerInfo.push(this.venue.address);
            }

            if(this.invoice.buyerId) {
                buyerInfo.push(this.$t('receipt.buyer_id') + ': ' + this.invoice.buyerId);
            }
            if(this.invoice.buyerCostCenterId) {
                buyerInfo.push(this.$t('receipt.buyer_cost_center') + ': ' + this.invoice.buyerCostCenterId);
            }

            return buyerInfo;
        },

        summary() {
            if(!this.sdcResponse) {
                return {
                    totalAmount: Money.parse(0),
                    totalTax: Money.parse(0),
                }
            }

            const totalAmount = this.order.getToPay().abs();
            const totalTax = this.order.getTax().abs();
            const totalPriceBeforeTax = this.order.getPriceBeforeTax().abs();

            return {
                totalAmount: totalAmount,
                totalTax: totalTax,
                totalPriceBeforeTax: totalPriceBeforeTax,
            }
        },
    },

    methods: {
        latToCyr(text) {
            return latToCyr(text)
        }
    }
};
</script>

<style lang="scss" scoped>
.receipt-invoice-sr {
    font-family: Arial, Helvetica, sans-serif;

    &.invoice {
        font-size: 13px;
        color: black;
        -webkit-user-select: text;
        -webkit-app-region: none;

        p {
            padding: 2px 0 !important;
            margin: 0 !important;
        }

        .divider {
            border-top: 1px solid black;
            height: 1px !important;
        }

        .receipt-header {
            padding: 10px;
            text-align: center;
            font-size: 16px;
            font-weight: 500;
        }

        .nonfiscal-heading {
            padding: 10px;
        }

        .receipt-subheader {
            width: 100%;
            padding: 10px;
            display: flex;
            flex-wrap: wrap-reverse;
            align-items: center;

            .receipt-details {
                flex: 1;
                min-width: 300px;
            }

            .receipt-buyer {
                border-radius: 4px;
                border: 1px solid black;
                width: 250px;
                padding: 10px;
                margin: 10px 10px 10px 0;
            }
        }

        .receipt-items {
            margin-top: 10px;
            border-collapse: collapse;
            width: 100%;

            tbody {

                tr {
                    vertical-align: top;

                    &.border-bottom {
                        border-bottom: 1px solid #b0b0b0;
                    }

                    td {
                        padding: 4px;

                        &:first-child {
                            padding-left: 10px;
                        }

                        &.name {
                            min-width: 130px;
                            word-break: break-word;
                            white-space: normal;
                        }

                        &:last-child {
                            padding-right: 10px;
                        }
                    }

                    &:hover {
                        background: #f7f7f7;
                    }
                }

                .heading-row {
                    padding: 0 5px;
                    vertical-align: middle;
                    border-top: 1px solid black;
                    border-bottom: 1px solid black;
                    font-size: 13px;
                    font-weight: 600;
                    background: #f1f1f1;
                }
            }
        }

        .tax-items {
            margin-top: 25px;
            border-collapse: collapse;
            width: 100%;

            tbody {

                tr {
                    td {
                        padding: 4px;
                    }

                    &:hover {
                        background: #f7f7f7;
                    }

                    &.bordered {
                        border: 1px solid black;
                        border-width: 1px 0;
                    }
                }

                .heading-row {
                    padding: 0 5px;
                    vertical-align: middle;
                    border-top: 1px solid black;
                    border-bottom: 1px solid black;
                    font-size: 13px;
                    font-weight: 600;
                    background: #f1f1f1;
                }
            }
        }

        .receipt-footer {
            display: flex;
            flex-wrap: wrap-reverse;
            margin-bottom: 20px;
            break-inside: avoid;

            .footer-item {
                flex: 1;

                &.additional-info {
                    padding: 10px 0 10px 10px;

                    .code {
                        display: block;
                    }

                    .mb-3 {
                        margin-bottom: 12px;
                    }

                    .buyer-signatures {
                        display: flex;
                        flex-wrap: wrap;

                        .buyer-signature {
                            margin-top: 20px;
                            text-align: center;

                            &:first-child {
                                margin-right: 20px;
                            }
                        }
                    }
                }

                &.qrcode {
                    max-width: 200px;
                    text-align: center;
                    padding: 10px;

                    .verification-link {
                        margin-top: 4px;
                    }
                }

                &.summary {
                    padding: 0px 10px 10px 0;

                    .summary-item {
                        padding: 1px;
                        display: flex;
                        justify-content: space-between;

                        &.to-pay {
                            font-weight: 500;
                            font-size: 18px;
                            display: flex;
                            align-items: center;
                        }

                        .amount {
                            text-align: right;
                        }
                    }
                }
            }
        }

        .nonfiscal-info {
            padding: 10px;
            white-space: pre-line;

            .signatures {
                display: flex;
                flex-wrap: wrap;

                .signature {
                    margin-top: 20px;
                    text-align: center;

                    &:first-child {
                        margin-right: 20px;
                    }
                }
            }
        }
    }
}
</style>
